import React, { useState, useEffect } from 'react';
import { DollarSign, Plus, X, Calculator, Save, Edit } from 'lucide-react';
import axios from 'axios';

const StaffSalaryTemplate = () => {
  const [staff, setStaff] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAllowanceDropdown, setShowAllowanceDropdown] = useState(false);
  const [showDeductionDropdown, setShowDeductionDropdown] = useState(false);
  
  const [salaryForm, setSalaryForm] = useState({
    basic_salary: '',
    allowances: [],
    deductions: [],
    effective_from: new Date().toISOString().split('T')[0]
  });

  const [calculations, setCalculations] = useState({
    total_allowances: 0,
    total_deductions: 0,
    gross_salary: 0,
    net_salary: 0
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchStaff();
    fetchComponents();
  }, []);

  useEffect(() => {
    calculateSalary();
  }, [salaryForm.basic_salary, salaryForm.allowances, salaryForm.deductions]);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/staff/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComponents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/salary/components`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComponents(response.data);
    } catch (error) {
      console.error('Error fetching components:', error);
    }
  };

  const fetchStaffSalary = async (staffId) => {
    try {
      const response = await axios.get(`${API_URL}/api/salary/templates/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        // Normalize allowances - handle both old 'type' and new 'calculation_type' field
        const normalizedAllowances = (response.data.allowances || []).map(a => ({
          ...a,
          calculation_type: a.calculation_type || a.type || 'fixed',
          value: a.value || 0
        }));
        
        // Normalize deductions
        const normalizedDeductions = (response.data.deductions || []).map(d => ({
          ...d,
          calculation_type: d.calculation_type || d.type || 'fixed',
          value: d.value || 0
        }));
        
        setSalaryForm({
          basic_salary: response.data.basic_salary || '',
          allowances: normalizedAllowances,
          deductions: normalizedDeductions,
          effective_from: response.data.effective_from || new Date().toISOString().split('T')[0]
        });
      } else {
        resetForm();
      }
    } catch (error) {
      console.error('Error fetching staff salary:', error);
      resetForm();
    }
  };

  const handleOpenModal = async (staffMember) => {
    setSelectedStaff(staffMember);
    setShowModal(true);
    await fetchStaffSalary(staffMember.id);
  };

  const resetForm = () => {
    setSalaryForm({
      basic_salary: '',
      allowances: [],
      deductions: [],
      effective_from: new Date().toISOString().split('T')[0]
    });
  };

  const calculateComponentValue = (component, basicSalary, grossSalary) => {
    const base = component.applicable_on === 'gross_salary' ? grossSalary : basicSalary;
    
    if (component.calculation_type === 'percentage') {
      return (base * parseFloat(component.value || 0)) / 100;
    }
    return parseFloat(component.value || 0);
  };

  const calculateSalary = () => {
    const basicSalary = parseFloat(salaryForm.basic_salary) || 0;
    
    // Calculate allowances first (they contribute to gross)
    let totalAllowances = 0;
    const updatedAllowances = salaryForm.allowances.map(allowance => {
      const value = parseFloat(allowance.value) || 0;
      let calculatedValue;
      
      if (allowance.calculation_type === 'percentage') {
        const base = allowance.applicable_on === 'gross_salary' ? basicSalary : basicSalary;
        calculatedValue = (base * value) / 100;
      } else {
        calculatedValue = value;
      }
      
      totalAllowances += calculatedValue;
      return { ...allowance, calculated_value: calculatedValue };
    });
    
    const grossSalary = basicSalary + totalAllowances;
    
    // Calculate deductions (can be based on gross)
    let totalDeductions = 0;
    const updatedDeductions = salaryForm.deductions.map(deduction => {
      const value = parseFloat(deduction.value) || 0;
      let calculatedValue;
      
      if (deduction.calculation_type === 'percentage') {
        const base = deduction.applicable_on === 'gross_salary' ? grossSalary : basicSalary;
        calculatedValue = (base * value) / 100;
      } else {
        calculatedValue = value;
      }
      
      totalDeductions += calculatedValue;
      return { ...deduction, calculated_value: calculatedValue };
    });
    
    const netSalary = grossSalary - totalDeductions;
    
    // Update form with calculated values (only if changed)
    if (JSON.stringify(updatedAllowances) !== JSON.stringify(salaryForm.allowances) ||
        JSON.stringify(updatedDeductions) !== JSON.stringify(salaryForm.deductions)) {
      setSalaryForm(prev => ({
        ...prev,
        allowances: updatedAllowances,
        deductions: updatedDeductions
      }));
    }
    
    setCalculations({
      total_allowances: totalAllowances,
      total_deductions: totalDeductions,
      gross_salary: grossSalary,
      net_salary: netSalary
    });
  };

  const addAllowance = (component) => {
    // Check if already added
    if (salaryForm.allowances.some(a => a.component_id === component.id)) {
      alert('This allowance is already added');
      setShowAllowanceDropdown(false);
      return;
    }
    
    const basicSalary = parseFloat(salaryForm.basic_salary) || 0;
    const value = parseFloat(component.default_value) || 0;
    
    let calculatedValue;
    if (component.calculation_type === 'percentage') {
      calculatedValue = (basicSalary * value) / 100;
    } else {
      calculatedValue = value;
    }
    
    const newAllowance = {
      component_id: component.id,
      name: component.name,
      calculation_type: component.calculation_type,
      value: component.default_value,
      applicable_on: component.applicable_on,
      calculated_value: calculatedValue
    };
    
    setSalaryForm(prev => ({
      ...prev,
      allowances: [...prev.allowances, newAllowance]
    }));
    setShowAllowanceDropdown(false);
  };

  const addDeduction = (component) => {
    // Check if already added
    if (salaryForm.deductions.some(d => d.component_id === component.id)) {
      alert('This deduction is already added');
      setShowDeductionDropdown(false);
      return;
    }
    
    const basicSalary = parseFloat(salaryForm.basic_salary) || 0;
    const totalAllowances = salaryForm.allowances.reduce((sum, a) => {
      const aVal = parseFloat(a.value) || 0;
      if (a.calculation_type === 'percentage') {
        return sum + (basicSalary * aVal) / 100;
      }
      return sum + aVal;
    }, 0);
    const grossSalary = basicSalary + totalAllowances;
    
    const value = parseFloat(component.default_value) || 0;
    
    let calculatedValue;
    if (component.calculation_type === 'percentage') {
      const base = component.applicable_on === 'gross_salary' ? grossSalary : basicSalary;
      calculatedValue = (base * value) / 100;
    } else {
      calculatedValue = value;
    }
    
    const newDeduction = {
      component_id: component.id,
      name: component.name,
      calculation_type: component.calculation_type,
      value: component.default_value,
      applicable_on: component.applicable_on,
      calculated_value: calculatedValue
    };
    
    setSalaryForm(prev => ({
      ...prev,
      deductions: [...prev.deductions, newDeduction]
    }));
    setShowDeductionDropdown(false);
  };

  const removeAllowance = (index) => {
    setSalaryForm(prev => ({
      ...prev,
      allowances: prev.allowances.filter((_, i) => i !== index)
    }));
  };

  const removeDeduction = (index) => {
    setSalaryForm(prev => ({
      ...prev,
      deductions: prev.deductions.filter((_, i) => i !== index)
    }));
  };

  const updateComponentValue = (type, index, value) => {
    setSalaryForm(prev => {
      const items = prev[type].map((item, i) => {
        if (i === index) {
          const basicSalary = parseFloat(prev.basic_salary) || 0;
          // Calculate current gross for deductions
          const totalAllowances = prev.allowances.reduce((sum, a, idx) => {
            const aVal = idx === index && type === 'allowances' ? parseFloat(value) || 0 : parseFloat(a.value) || 0;
            if (a.calculation_type === 'percentage') {
              // Use applicable_on to determine base for allowance calculation
              const aBase = a.applicable_on === 'gross_salary' ? basicSalary : basicSalary;
              return sum + (aBase * aVal) / 100;
            }
            return sum + aVal;
          }, 0);
          const grossSalary = basicSalary + totalAllowances;
          
          const newValue = parseFloat(value) || 0;
          let calculatedValue;
          if (item.calculation_type === 'percentage') {
            // FIX: Use applicable_on field to determine the base (basic_salary or gross_salary)
            const base = item.applicable_on === 'gross_salary' ? grossSalary : basicSalary;
            calculatedValue = (base * newValue) / 100;
          } else {
            calculatedValue = newValue;
          }
          
          return { ...item, value: value, calculated_value: calculatedValue };
        }
        return item;
      });
      
      return { ...prev, [type]: items };
    });
  };

  const handleSubmit = async () => {
    if (!selectedStaff) return;
    
    if (!salaryForm.basic_salary || parseFloat(salaryForm.basic_salary) <= 0) {
      alert('Please enter a valid basic salary');
      return;
    }

    setSaving(true);
    
    try {
      const templateData = {
        basic_salary: parseFloat(salaryForm.basic_salary),
        allowances: salaryForm.allowances,
        deductions: salaryForm.deductions,
        effective_from: salaryForm.effective_from
      };

      console.log('Saving salary template for staff:', selectedStaff.id);
      console.log('Template data:', templateData);

      const response = await axios.post(
        `${API_URL}/api/salary/templates/${selectedStaff.id}`,
        templateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Save successful:', response.data);
      alert('Salary template saved successfully!');
      setShowModal(false);
      fetchStaff(); // Refresh staff list
    } catch (error) {
      console.error('Error saving salary template:', error);
      let errorMessage = 'Failed to save salary template';
      
      if (error.response) {
        // Server responded with error
        errorMessage = error.response.data?.detail || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request made but no response
        errorMessage = 'Network error: Unable to reach server. Please check your connection.';
      } else {
        // Other error
        errorMessage = error.message || 'An unexpected error occurred';
      }
      
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const allowanceComponents = components.filter(c => c.type === 'allowance' && c.is_active);
  const deductionComponents = components.filter(c => c.type === 'deduction' && c.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading staff...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Staff Salary Management</h1>
        <p className="text-sm text-gray-500 mt-1">Assign salary templates to staff members</p>
      </div>

      {/* Staff List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Staff Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Employee ID</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Department</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Current Salary</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {staff.map(member => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-900">{member.full_name}</p>
                    <p className="text-sm text-gray-500">{member.email}</p>
                  </td>
                  <td className="px-6 py-4 text-gray-700">{member.employee_id}</td>
                  <td className="px-6 py-4 text-gray-600">{member.department || 'N/A'}</td>
                  <td className="px-6 py-4">
                    {member.salary ? (
                      <span className="font-semibold text-green-700">₹{member.salary.toLocaleString('en-IN')}</span>
                    ) : (
                      <span className="text-gray-400">Not Set</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleOpenModal(member)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      <Edit size={16} />
                      {member.salary ? 'Edit Salary' : 'Set Salary'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Salary Configuration Modal */}
      {showModal && selectedStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Configure Salary Template</h3>
                <p className="text-sm text-gray-600">{selectedStaff.full_name} - {selectedStaff.employee_id}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Salary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Basic Salary <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-500">₹</span>
                  <input
                    type="number"
                    value={salaryForm.basic_salary}
                    onChange={(e) => setSalaryForm(prev => ({ ...prev, basic_salary: e.target.value }))}
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="30000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Allowances */}
                <div className="border rounded-lg p-4 bg-green-50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-green-800">Allowances (+)</h4>
                    <div className="relative">
                      <button 
                        onClick={() => setShowAllowanceDropdown(!showAllowanceDropdown)}
                        className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1"
                      >
                        <Plus size={16} /> Add
                      </button>
                      {showAllowanceDropdown && (
                        <>
                          <div 
                            className="fixed inset-0 z-10" 
                            onClick={() => setShowAllowanceDropdown(false)}
                          />
                          <div className="absolute right-0 mt-2 w-64 bg-white border rounded-lg shadow-xl z-20">
                            <div className="p-2 max-h-60 overflow-y-auto">
                              {allowanceComponents.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No allowances available</p>
                              ) : (
                                allowanceComponents.map(comp => (
                                  <button
                                    key={comp.id}
                                    onClick={() => addAllowance(comp)}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm"
                                  >
                                    <p className="font-medium">{comp.name}</p>
                                    <p className="text-xs text-gray-500">
                                      {comp.calculation_type === 'percentage' ? `${comp.default_value}%` : `₹${comp.default_value}`}
                                    </p>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {salaryForm.allowances.map((allowance, index) => (
                      <div key={index} className="bg-white border rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{allowance.name}</span>
                          <button onClick={() => removeAllowance(index)} className="text-red-600 hover:text-red-800">
                            <X size={16} />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={allowance.value}
                            onChange={(e) => updateComponentValue('allowances', index, e.target.value)}
                            className="flex-1 px-2 py-1 border rounded text-sm"
                          />
                          <span className="text-sm text-gray-500">
                            {allowance.calculation_type === 'percentage' ? '%' : '₹'}
                          </span>
                          <span className="text-sm font-semibold text-green-700">
                            = ₹{allowance.calculated_value?.toFixed(2) || 0}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Deductions */}
                <div className="border rounded-lg p-4 bg-red-50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-red-800">Deductions (-)</h4>
                    <div className="relative">
                      <button 
                        onClick={() => setShowDeductionDropdown(!showDeductionDropdown)}
                        className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center gap-1"
                      >
                        <Plus size={16} /> Add
                      </button>
                      {showDeductionDropdown && (
                        <>
                          <div 
                            className="fixed inset-0 z-10" 
                            onClick={() => setShowDeductionDropdown(false)}
                          />
                          <div className="absolute right-0 mt-2 w-64 bg-white border rounded-lg shadow-xl z-20">
                            <div className="p-2 max-h-60 overflow-y-auto">
                              {deductionComponents.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No deductions available</p>
                              ) : (
                                deductionComponents.map(comp => (
                                  <button
                                    key={comp.id}
                                    onClick={() => addDeduction(comp)}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm"
                                  >
                                    <p className="font-medium">{comp.name}</p>
                                    <p className="text-xs text-gray-500">
                                      {comp.calculation_type === 'percentage' ? `${comp.default_value}%` : `₹${comp.default_value}`}
                                    </p>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {salaryForm.deductions.map((deduction, index) => (
                      <div key={index} className="bg-white border rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{deduction.name}</span>
                          <button onClick={() => removeDeduction(index)} className="text-red-600 hover:text-red-800">
                            <X size={16} />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={deduction.value}
                            onChange={(e) => updateComponentValue('deductions', index, e.target.value)}
                            className="flex-1 px-2 py-1 border rounded text-sm"
                          />
                          <span className="text-sm text-gray-500">
                            {deduction.calculation_type === 'percentage' ? '%' : '₹'}
                          </span>
                          <span className="text-sm font-semibold text-red-700">
                            = ₹{deduction.calculated_value?.toFixed(2) || 0}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Salary Summary */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calculator className="text-blue-600" size={20} />
                  <h4 className="font-semibold text-blue-900">Salary Calculation</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Basic Salary:</span>
                    <span className="font-semibold">₹{parseFloat(salaryForm.basic_salary || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-green-700">
                    <span>Total Allowances:</span>
                    <span className="font-semibold">+ ₹{calculations.total_allowances.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-medium">Gross Salary:</span>
                    <span className="font-semibold text-lg">₹{calculations.gross_salary.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-red-700">
                    <span>Total Deductions:</span>
                    <span className="font-semibold">- ₹{calculations.total_deductions.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t-2 border-blue-300">
                    <span className="font-bold text-blue-900">Net Salary:</span>
                    <span className="font-bold text-xl text-blue-900">₹{calculations.net_salary.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>

              {/* Effective Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Effective From
                </label>
                <input
                  type="date"
                  value={salaryForm.effective_from}
                  onChange={(e) => setSalaryForm(prev => ({ ...prev, effective_from: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : 'Save Salary Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffSalaryTemplate;
